import { Download, FileText, Trash2, Share2 } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { AnalyticsConsentCard } from "../components/AnalyticsConsentCard";
import { PrivacyDisclosure } from "../components/PrivacyDisclosure";
import { ExportReadinessPanel } from "../components/ExportReadinessPanel";
import { ProgressModal } from "../components/ProgressModal";
import { SupportProjectSection } from "../components/SupportProjectSection";
import { formatIssueCount, getExportReadiness } from "../services/app/reportStatus";
import {
  trackExportScreenOpened,
  trackPdfDownloadClicked,
  trackPdfGenerateFailed,
  trackPdfGenerateStarted,
  trackPdfGenerateSuccess,
  type ReportCounts
} from "../services/analytics/analyticsEvents";
import { deleteReport, markReportDownloaded } from "../services/db/reportRepo";
import type { Issue, Report, Room } from "../services/db/schema";
import { formatBytes } from "../services/app/validation";
import { useNetworkStatus } from "../services/sync/networkStatus";
import { isNativeApp } from "../services/platform/runtime";
import { openPdf, savePdf, sharePdf } from "../services/platform/nativeShare";
import { isAndroidApp } from "../services/platform/androidActions";

import { commerceEnabled } from "../services/commerce/config";
const ReportPurchase = lazy(() => import("../components/ReportPurchase").then((module) => ({ default: module.ReportPurchase })));

interface ExportRouteProps {
  report: Report;
  rooms: Room[];
  issues: Issue[];
  onChanged: () => void;
  onDeleted: () => void;
}

interface PdfState {
  blob: Blob;
  url: string;
  filename: string;
  sizeBytes: number;
}

export function ExportRoute({
  report,
  rooms,
  issues,
  onChanged,
  onDeleted
}: ExportRouteProps): JSX.Element {
  const online = useNetworkStatus();
  const [purchased, setPurchased] = useState(false);
  const canDownload = !commerceEnabled || purchased;
  const [exportAnyway, setExportAnyway] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ message: "", value: 0 });
  const [pdf, setPdf] = useState<PdfState>();
  const [error, setError] = useState<string>();
  const [pdfAction, setPdfAction] = useState<"open" | "save" | "share">();
  const actionPending = useRef(false);
  const [saveMessage, setSaveMessage] = useState<string>();
  const pdfReadyRef = useRef<HTMLButtonElement>(null);
  const readiness = useMemo(() => getExportReadiness(issues), [issues]);
  const incomplete = readiness.incomplete;
  const canGeneratePdf =
    issues.length > 0 &&
    readiness.blockingReasonCount === 0 &&
    (incomplete.length === 0 || (readiness.canContinueWithoutNote && exportAnyway));
  const exportRooms = useMemo(() => {
    const roomIdsWithNotes = new Set(issues.map((issue) => issue.roomId));
    return rooms.filter((room) => roomIdsWithNotes.has(room.id));
  }, [issues, rooms]);
  const ready = issues.length - incomplete.length;
  const reportCounts = useMemo(
    () => buildReportCounts(exportRooms.length, issues, ready, incomplete.length),
    [exportRooms.length, incomplete.length, issues, ready]
  );

  useEffect(
    () => () => {
      if (pdf?.url) {
        URL.revokeObjectURL(pdf.url);
      }
    },
    [pdf?.url]
  );

  useEffect(() => {
    trackExportScreenOpened(reportCounts);
  }, [reportCounts]);

  useEffect(() => {
    if (pdf?.url) pdfReadyRef.current?.scrollIntoView({ block: "center" });
  }, [pdf?.url]);

  async function generatePdf() {
    if (actionPending.current || generating) return;
    setError(undefined);
    setSaveMessage(undefined);
    if (!canGeneratePdf) {
      setError("Finish queued, failed, or unreviewed condition notes before generating the PDF.");
      return;
    }
    setGenerating(true);
    const startedAt = performance.now();
    trackPdfGenerateStarted(reportCounts);
    try {
      const { generateReportPdf } = await import("../services/pdf/generateReportPdf");
      const result = await generateReportPdf({
        report: { ...report, optionalPropertyAddress: undefined },
        rooms: exportRooms,
        issues,
        includeIncomplete: exportAnyway,
        onProgress: (event) => setProgress({ message: event.message, value: event.progress })
      });
      if (pdf?.url) {
        URL.revokeObjectURL(pdf.url);
      }
      const url = URL.createObjectURL(result.blob);
      setPdf({ blob: result.blob, url, filename: result.filename, sizeBytes: result.blob.size });
      trackPdfGenerateSuccess(reportCounts, {
        exportBytes: result.blob.size,
        generationMs: Math.round(performance.now() - startedAt),
        pageCount: result.pageCount
      });
    } catch (caught) {
      trackPdfGenerateFailed(caught, reportCounts, Math.round(performance.now() - startedAt));
      setError(caught instanceof Error ? caught.message : "Could not generate the PDF.");
    } finally {
      setGenerating(false);
    }
  }

  async function removeDraft() {
    if (!window.confirm("Delete this local draft from this device?")) {
      return;
    }
    await deleteReport(report.id);
    onDeleted();
  }

  async function usePdf(action: "open" | "save" | "share"): Promise<void> {
    if (!pdf || actionPending.current || generating) return;
    actionPending.current = true;
    setPdfAction(action);
    setError(undefined);
    setSaveMessage(undefined);
    try {
      if (action === "open") await openPdf(pdf.blob, pdf.filename);
      else if (action === "share") await sharePdf(pdf.blob, pdf.filename);
      else if (await savePdf(pdf.blob, pdf.filename)) {
        setSaveMessage("PDF saved to your chosen folder.");
        // A confirmed native write is evidence of export; opening/sharing alone is not.
        await markReportDownloaded(report.id).then(onChanged).catch(() => undefined);
      }
    } catch (caught) {
      const code = caught && typeof caught === "object" && "code" in caught ? caught.code : undefined;
      if (action === "open") setError(code === "OS-PLUG-FLVW-0010"
        ? "This phone has no PDF viewer. You can still save the PDF or share it with a device that can open PDFs."
        : "Could not open the PDF. Try Save PDF or Share PDF.");
      else if (action === "save") setError("Could not save the PDF. Check free storage and choose a folder again.");
      else setSaveMessage("Your PDF is ready. You can share it again at any time.");
    } finally { actionPending.current = false; setPdfAction(undefined); }
  }

  return (
    <section className="screen">
      <div className="topbar">
        <div className="topbar__title">
          <h1>Export</h1>
          <p>
            {exportRooms.length} {exportRooms.length === 1 ? "room" : "rooms"} with {formatIssueCount(issues.length)}
          </p>
        </div>
      </div>

      <div className="export-summary">
        <div>
          <span>Rooms with notes</span>
          <strong>{exportRooms.length}</strong>
        </div>
        <div>
          <span>Condition Notes</span>
          <strong>{issues.length}</strong>
        </div>
        <div>
          <span>Ready for PDF</span>
          <strong>{ready}</strong>
        </div>
      </div>

      <ExportReadinessPanel
        issues={issues}
        exportAnyway={exportAnyway}
        online={online}
        onExportAnywayChange={setExportAnyway}
      />

      {error && <div className="notice notice--error">{error}</div>}
      {saveMessage && <div className="notice" role="status">{saveMessage}</div>}

      <div className="button-row export-actions">
        <button
          className="button button--primary"
          type="button"
          disabled={generating || Boolean(pdfAction) || !canGeneratePdf}
          onClick={() => void generatePdf()}
        >
          {pdf ? "Regenerate PDF" : "Generate PDF"}
        </button>
        {pdf && canDownload && isAndroidApp() && <button className="button button--primary" type="button" disabled={Boolean(pdfAction) || generating} onClick={() => void usePdf("save")}>
          <Download size={18} aria-hidden="true" /> {pdfAction === "save" ? "Saving PDF..." : "Save PDF"}
        </button>}
        {pdf && canDownload && isNativeApp() && <button className="button" type="button" disabled={Boolean(pdfAction) || generating} onClick={() => void usePdf("share")}>
          <Share2 size={18} aria-hidden="true" /> {pdfAction === "share" ? "Preparing PDF..." : isAndroidApp() ? "Share PDF" : "Save or share PDF"}
        </button>}
        {pdf && canDownload && !isNativeApp() && (
          <a
            className="button button--primary"
            href={pdf.url}
            download={pdf.filename}
            onClick={() => {
              trackPdfDownloadClicked(reportCounts, pdf.sizeBytes);
              void markReportDownloaded(report.id).then(onChanged);
            }}
          >
            <Download size={18} aria-hidden="true" />
            Download PDF
          </a>
        )}
      </div>

      {pdf && canDownload && isNativeApp() && (
        <button ref={pdfReadyRef} className="pdf-ready pdf-ready--open" type="button" disabled={Boolean(pdfAction) || generating} onClick={() => void usePdf("open")}>
          <span className="pdf-ready__icon" aria-hidden="true">
            <FileText size={40} />
          </span>
          <span>
            <strong>{pdfAction === "open" ? "Opening PDF..." : "Open PDF"}</strong>
            <span className="pdf-ready__filename">
              {pdf.filename} - {formatBytes(pdf.sizeBytes)}
            </span>
          </span>
        </button>
      )}
      {pdf && canDownload && !isNativeApp() && <a className="pdf-ready" href={pdf.url} target="_blank" rel="noopener noreferrer"><FileText size={32} aria-hidden="true" />Open PDF · {formatBytes(pdf.sizeBytes)}</a>}

      {commerceEnabled && <Suspense fallback={<p>Opening checkout...</p>}><ReportPurchase reportId={report.id} pdf={pdf?.blob} onPaid={setPurchased} /></Suspense>}
      {pdf && !commerceEnabled && <SupportProjectSection />}

      {pdf && (
        <button className="button button--danger" type="button" onClick={() => void removeDraft()}>
          <Trash2 size={18} aria-hidden="true" />
          Delete local draft
        </button>
      )}

      <AnalyticsConsentCard />
      <PrivacyDisclosure />

      {generating && (
        <ProgressModal title="Generating PDF" message={progress.message} progress={progress.value} />
      )}
    </section>
  );
}

function buildReportCounts(
  roomCount: number,
  issues: Issue[],
  readyConditionCount: number,
  incompleteConditionCount: number
): ReportCounts {
  return {
    roomCount,
    conditionCount: issues.length,
    readyConditionCount,
    incompleteConditionCount,
    mediaItemCount: issues.length,
    mediaBytesTotal: issues.reduce((sum, issue) => sum + issue.photoSizeBytes, 0),
    totalCompressedMediaBytes: issues.reduce((sum, issue) => sum + issue.photoSizeBytes, 0),
    thumbnailBytes: issues.reduce((sum, issue) => sum + issue.thumbSizeBytes, 0)
  };
}
